import { Button, Text } from '@/components'
import { useEffect, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { match } from 'ts-pattern'
import confetti from 'canvas-confetti'
import { useDispatch } from 'react-redux'
import Celebrate from '@/app/img/celebrate.svg'
import Nope from '@/app/img/nope.svg'
import ThemedDocumentRenderer from '@/components/ThemedDocumentRenderer/ThemedDocumentRenderer'
import { useMultiProgramRouteContext } from '..'
import { useCurrentMultiProgramFlowContext } from '@/app/context/MultiProgram/MultiProgramFlowContexts'
import MarketingPixelTracking from '@/components/MarketingPixelTracking/MarketingPixelTracking'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { useInitConfigureDeviceFlow } from '@/app/pages/configure-devices/useInitConfigureDeviceFlow'
import useEnrolledDeviceIds from '@/app/hooks/useEnrolledDeviceIds'
import { enrolledUtilityProgramsCollection } from '@/reducers/enrolledUtilityPrograms'
import { isNativeMobile } from '@/authenticated/hooks/useIsMobile'

export type EnrollmentState = 'pending' | 'error'
export default function EnrollmentState() {
  const { state } = useParams<{
    id: string
    state: EnrollmentState
  }>()
  const { utilityProgram } = useMultiProgramRouteContext()
  const postApplicationDocument = useMemo(
    () => utilityProgram.view_config_json.postApplicationDocument as object,
    [utilityProgram],
  )
  const { onFlowComplete } = useCurrentMultiProgramFlowContext()
  const dispatch = useDispatch()
  const postEnrollmentSetupEnabled = useFeatureFlag({
    flag: 'post_enrollment_device_setup',
    experimentalValue: true,
  })
  const { initFlow } = useInitConfigureDeviceFlow()
  const { deviceIds: enrolledDeviceIds } = useEnrolledDeviceIds(
    utilityProgram.id,
  )

  // Refresh enrolled programs so we have up-to-date device enrollment data
  // before launching the configure-devices flow.
  useEffect(() => {
    dispatch(enrolledUtilityProgramsCollection.actions.invalidate())
  }, [dispatch])

  const hasConfigurableDevices =
    enrolledDeviceIds.length > 0 || !isNativeMobile()
  const useDeviceSetupCta =
    state === 'pending' && postEnrollmentSetupEnabled && hasConfigurableDevices

  const onContinue = () => {
    if (useDeviceSetupCta) {
      initFlow({
        deviceIds: enrolledDeviceIds,
        setupMobile: true,
        onComplete: onFlowComplete,
      })
      return
    }
    onFlowComplete()
  }

  const content = useMemo(
    () =>
      match(state)
        .with('pending', () => (
          <>
            <PendingState postApplicationDocument={postApplicationDocument} />
            <MarketingPixelTracking
              event="PROGRAMENROLLMENT"
              subid={String(utilityProgram.id)}
            />
          </>
        ))
        .with('error', () => (
          <ErrorState
            programName={
              utilityProgram.view_config_json?.name ?? utilityProgram.name
            }
          />
        ))
        .exhaustive(),
    [utilityProgram, state, postApplicationDocument],
  )
  return (
    <div className="flex h-full w-full flex-col [&>*]:text-center">
      {/* Content fills the available space and centers vertically; the
          CTA stays pinned to the bottom of the screen so the layout
          matches the Figma success screen on mobile. */}
      <div className="flex flex-1 flex-col justify-center gap-8">{content}</div>
      <Button id={'continue'} onClick={onContinue}>
        {useDeviceSetupCta ? 'Set Up My Device(s)' : 'Continue'}
      </Button>
    </div>
  )
}

function PendingState({
  postApplicationDocument,
}: {
  postApplicationDocument?: object
}) {
  const hasFired = useRef(false)
  useEffect(() => {
    if (hasFired.current) {
      return
    }
    hasFired.current = true
    // Left side confetti
    confetti({
      particleCount: 100,
      spread: 70,
      startVelocity: 60,
      origin: { x: 0.1, y: 0.9 },
    })
    // Right side confetti
    confetti({
      particleCount: 100,
      spread: 70,
      startVelocity: 60,
      origin: { x: 0.9, y: 0.9 },
    })

    return () => {
      // necessary so confetti doesn't launch on random un/re-mounts
      confetti.reset()
    }
  }, [])

  return (
    <>
      <img
        src={Celebrate}
        alt={'person celebrating'}
        className={'w-2/3 self-center'}
      />
      {postApplicationDocument && (
        <ThemedDocumentRenderer document={postApplicationDocument} />
      )}
    </>
  )
}

function ErrorState({ programName }: { programName: string }) {
  return (
    <>
      <img src={Nope} alt={'error image'} className={'w-2/3 self-center'} />
      <Text variant="h3">
        Oh no! You were not successfully enrolled in the {programName}
      </Text>
      <Text variant="body2">Please try again later.</Text>
    </>
  )
}
